import type { Currency } from "~/types/currencies";
import { getCurrentCurrency, BASE_CURRENCY } from "~/composables/CURRENCIES";
import {
  Api,
  type UpdateUserDTO,
  type CreateReviewDTO,
  type UserResponseDTO,
  type UnitResponseDTO,
  type UpdateReviewDTO,
  type AuthenticateDTO,
  type PortfolioDTO,
  type UpsertPortfolioManualTokenDTO,
} from "~/services/swagger/Api";
import { Notification } from "~/services/notifications/toast";
import { jwtDecode } from "jwt-decode";
import { apiPost } from "~/services/api";
import getRoute from "~/utilities/configs/apiMap";
import {
  getLocalStorage,
  writePersistentLocalStorage,
} from "~/services/LocalStorage/localStorage";
import { PopUpServices } from "~/services/PopUp/callPopUp";
import { userNames } from "~/utilities/constants/userNames";

interface IPermission {
  paths: string[];
}

type Role = "default" | "admin" | "guest" | "service";

const apiClient = new Api();
const nuxtApp = useNuxtApp();

export const userStore = defineStore("userStore", {
  state: () => ({
    currentCurrency: getCurrentCurrency(BASE_CURRENCY.value.code) as Currency,
    role: "" as Role,
    id: "",
    avatar: "",
    permissions: {
      paths: [],
    } as IPermission,
    isInSystem: false,
    favoriteEstates: [] as UnitResponseDTO[],
    favoriteLands: [] as UnitResponseDTO[],
    currentUser: {} as UserResponseDTO,
    userPortfolio: {} as PortfolioDTO,
    portfolioLoading: false,
  }),
  actions: {
    getPermissions() {
      this.getPermissionPaths();
    },
    getPermissionPaths() {
      if (this.role === "admin") {
        this.permissions.paths = ["admin", ...this.permissions.paths];
      }
    },
    async fetchUserData() {
      try {
        const data = await apiClient.api.usersControllerGetMe();
        if (!data.ok) return;
        this.role = data.data.role;
        this.avatar = data.data.avatarFile?.url ?? "";
        this.getPermissions();
        this.isInSystem = true;
        this.favoriteEstates = data.data.units.filter(
          (item) => item.type === "Estate"
        );
        this.favoriteLands = data.data.units.filter(
          (item) => item.type === "Land"
        );
        this.id = data.data.id;
        this.currentUser = data.data;
        this.createDefaultUserName();
        // web3OnboardManager.restoreWallet();
        return data.data;
      } catch (error) {
        console.error(error);
        console.log("error: ", error);
        const errorMessage =
          (error as Error).message || nuxtApp.$i18n.t("errors.catch_all");
        createErrorNotify({ message: errorMessage });
      }
    },
    async updateUserData(updatedUser: UpdateUserDTO) {
      try {
        const data = await apiClient.api.usersControllerUpdate(updatedUser);
        if (!data.ok) return;
        // this.fetchUserData();
        return data;
      } catch (error) {
        console.error(error);
        createErrorNotify({
          message: nuxtApp.$i18n.t("errors.catch_all"),
          delay: 3000,
        });
        return null;
      }
    },
    async userSignOut() {
      try {
        const data = await apiClient.api.authControllerSignOut();
        if (!data.ok) return;
        const token = useCookie("acc_token");
        token.value = null;
        this.role = "default";
        this.avatar = "";
        this.permissions.paths.length = 0;
        this.isInSystem = false;
        const localePath = useLocalePath();
        const route = localePath("/");
        navigateTo(route);
      } catch (error) {
        console.error(error);
        const errorMessage =
          (error as Error).message || nuxtApp.$i18n.t("errors.catch_all");
        createErrorNotify({ message: errorMessage, delay: 3000 });
      }
    },
    async changePassword(args: {
      password: string;
      oldPassword: string;
      isEmailSent: boolean;
    }) {
      if (args.isEmailSent) {
        this.changePasswordConfirm({
          password: args.password,
          token: args.oldPassword,
        });
        return;
      }
      try {
        const passwordChangeRes =
          await apiClient.api.authNativeControllerPasswordChange({
            password: args.password,
            oldPassword: args.oldPassword,
          });
        if (!passwordChangeRes.ok) {
          return;
        }
      } catch (error) {
        console.error(error);
        const errorMessage =
          (error as Error).message || nuxtApp.$i18n.t("errors.catch_all");
        createErrorNotify({ message: errorMessage, delay: 3000 });
      }
    },
    async changePasswordConfirm(args: { password: string; token: string }) {
      try {
        const passwordChangeRes =
          await apiClient.api.authNativeControllerPasswordChangeConfirm({
            password: args.password,
            token: args.token,
          });
        if (!passwordChangeRes.ok) {
          return;
        }
      } catch (error) {
        console.error(error);
        const errorMessage =
          (error as Error).message || nuxtApp.$i18n.t("errors.catch_all");
        createErrorNotify({ message: errorMessage, delay: 3000 });
      }
    },
    async loginUser(args: {
      email: string;
      password: string;
      recaptcha: string | null;
    }) {
      try {
        const loginResponse = await apiClient.api.authNativeControllerSignIn(
          {
            login: args.email,
            password: args.password,
          },
          { headers: { recaptcha: args.recaptcha } }
        );
        if (!loginResponse.ok) {
          createErrorNotify({ message: "Email or password is incorrect" });
          return false;
        }
        const token = useCookie("acc_token");
        const refreshToken = useCookie("acc_refresh_token");
        token.value = loginResponse.data?.accessToken;
        refreshToken.value = loginResponse.data.refreshToken;
        this.isInSystem = true;
        return true;
      } catch (error) {
        console.error(error);
        createErrorNotify({ message: "Email or password is incorrect" });
        return false;
      }
    },
    async signUpUser(args: {
      email: string;
      password: string;
      userName: string;
      isMailTokenRecived: boolean;
      recaptcha?: string;
    }) {
      if (args.isMailTokenRecived) {
        const res = await this.checkSignUpEmailToken({
          email: args.email,
          // userName:args.userName,
          token: args.password,
          recaptcha: args.recaptcha,
        });
        this.isInSystem = Boolean(res);
        return res;
      }
      try {
        const SignUpResponse = await apiClient.api.authNativeControllerSignUp({
          username: args.userName,
          email: args.email,
          password: args.password,
        });
        if (!SignUpResponse.ok) return false;
        const emailSent = new Notification({
          type: "system",
          message: nuxtApp.$i18n.t("login.log_check_email"),
          timeout: 5000,
        });
        emailSent.createNewToast();
        return true;
      } catch (error) {
        console.error(error);
        const e = (error as Error).message;
        if (e.includes("user")) return;
        // const errorMessage = e || nuxtApp.$i18n.t("errors.catch_all");
        // createErrorNotify({ message: errorMessage });
      }
    },
    async checkSignUpEmailToken(args: {
      email: string;
      token: string;
      recaptcha?: string;
    }) {
      try {
        const loginResponse =
          await apiClient.api.authNativeControllerSignUpConfirm(
            {
              email: args.email,
              token: args.token,
            },
            { headers: { recaptcha: args.recaptcha } }
          );
        if (!loginResponse.ok) {
          return false;
        }
        const token = useCookie("acc_token");
        const refreshToken = useCookie("acc_refresh_token");
        token.value = loginResponse.data?.accessToken;
        refreshToken.value = loginResponse.data?.refreshToken;
        this.isInSystem = true;
        return true;
      } catch (error) {
        console.error(error);
        const errorMessage =
          (error as Error).message || nuxtApp.$i18n.t("errors.catch_all");
        createErrorNotify({ message: errorMessage });
      }
    },
    async refreshAccToken() {
      const refreshToken = useCookie("acc_refresh_token");
      const token = useCookie("acc_token");
      try {
        const refreshResponse = await apiClient.api.authControllerRefresh({
          headers: {
            Authorization: `Bearer ${refreshToken.value}`,
          },
        });
        if (!refreshResponse.ok) {
          return;
        }
        token.value = refreshResponse.data?.accessToken;
        refreshToken.value = refreshResponse.data?.refreshToken;
        return true;
      } catch (error) {
        console.error(error);
        const errorMessage =
          (error as Error).message || nuxtApp.$i18n.t("errors.catch_all");
        createErrorNotify({ message: errorMessage });
        return null;
      }
    },
    async checkAccTokenExp(token: string) {
      const decodedToken: any = jwtDecode(token);
      const currentTime = Math.floor(Date.now() / 1000);

      if (decodedToken.exp < currentTime) {
        const res = await this.refreshAccToken();
        if (!res) {
          const token = useCookie("acc_token");
          const refreshToken = useCookie("acc_refresh_token");
          token.value = null;
          refreshToken.value = null;
          createErrorNotify({
            message: "You session is expired. Please login again.",
          });
        }
        return res;
      } else {
        this.isInSystem = true;
        return true;
      }
    },

    async refreshEmailSingUp(email: string) {
      try {
        const refreshRes = await apiClient.api.authNativeControllerSignUpResend(
          {
            email: email,
          }
        );
        if (!refreshRes.ok) {
          return null;
        }
      } catch (error) {
        console.error(error);
        const errorMessage =
          (error as Error).message || nuxtApp.$i18n.t("errors.catch_all");
        createErrorNotify({ message: errorMessage });
      }
    },
    async restoreUserAccount(args: { email: string }) {
      try {
        await apiClient.api.authNativeControllerPasswordRecovery({
          email: args.email,
        });
        return true;
      } catch (error) {
        console.error(error);
        createErrorNotify({
          message: nuxtApp.$i18n.t("errors.catch_all"),
        });
        return false;
      }
    },
    async restoreUserAccountConfirm(args: {
      email: string;
      token: string;
      password: string;
    }) {
      try {
        await apiClient.api.authNativeControllerPasswordRecoveryConfirm({
          email: args.email,
          token: args.token,
          password: args.password,
        });
        await this.loginUser({ email: args.email, password: args.password });
        const localePath = useLocalePath();
        const route = localePath("/profile");
        navigateTo(route);
        return true;
      } catch (error) {
        console.error(error);
        createErrorNotify({
          message: "Code from email is not valid.",
        });
        return false;
      }
    },
    async singInWithGoogle(code: string, silent?: boolean) {
      const query = { code: code };
      const headers = { Authorization: `Bearer ${code}` };
      if (silent) {
        delete query.code;
      } else {
        delete headers.Authorization;
      }
      try {
        const res = await apiClient.api.authGoogleControllerAuth({
          query: query,
          headers: headers,
        });
        if (!res.ok) return;
        const token = useCookie("acc_token");
        const refreshToken = useCookie("acc_refresh_token");
        token.value = res.data?.accessToken;
        refreshToken.value = res.data?.refreshToken;
        this.isInSystem = true;
        if (!silent) {
          const localePath = useLocalePath();
          navigateTo(localePath("/"));
        }
      } catch (error) {
        console.error(error);
        const errorMessage =
          (error as Error).message || nuxtApp.$i18n.t("errors.catch_all");
        createErrorNotify({ message: errorMessage });
      }
    },
    initGoogleAuth() {
      if (window.google) {
        const state = this;
        window.google.accounts?.id?.initialize({
          client_id:
            "15908513748-0b98se70d10cp7gl9d3a59iper6rf8h6.apps.googleusercontent.com",
          callback: state.handleCredentialResponse,
          auto_select: true,
          use_fedcm_for_prompt: true,
        });
      }
    },
    async handleCredentialResponse(response: any) {
      await this.singInWithGoogle(response.credential, true);
      const usePopUpServices = PopUpServices();
      usePopUpServices.popUpRef?.hide();
    },

    async leaveReview(id: string, review: CreateReviewDTO) {
      try {
        const { $recaptcha } = useNuxtApp();
        const token = await $recaptcha.execute("login");
        const res = await apiClient.api.reviewsControllerCreate(id, review, {
          headers: {
            recaptcha: token,
            Authorization: `Bearer ${useCookie("acc_token").value}`,
          },
        });
        if (!res.ok) {
          createErrorNotify({
            message: "Some error occured,please try later.",
          });
          return null;
        }
        return res.data;
      } catch (error) {
        console.error(error);
        createErrorNotify({ message: "Some error occured,please try later." });
        return null;
      }
    },
    async updateReview(
      unitId: string,
      reviewId: string,
      review: UpdateReviewDTO
    ) {
      try {
        const { $recaptcha } = useNuxtApp();
        const token = await $recaptcha.execute("login");
        const res = await apiClient.api.reviewsControllerUpdate(
          unitId,
          reviewId,
          review,
          {
            headers: {
              recaptcha: token,
              Authorization: `Bearer ${useCookie("acc_token").value}`,
            },
          }
        );
        if (!res.ok) {
          createErrorNotify({
            message: "Some error occured,please try later.",
          });
          return null;
        }
        return res.data;
      } catch (error) {
        console.error(error);
        createErrorNotify({ message: "Some error occured,please try later." });
        return null;
      }
    },
    async checkUserName(name: string) {
      try {
        const response = await apiPost({
          url: getRoute({ endpont: "post_check_username" }),
          body: { username: name },
        });
        return response;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    generateUserUID() {
      if (typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
      }
      return `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
    },
    createUserUID() {
      const existingUID = getLocalStorage("user_uid");
      if (existingUID) {
        return existingUID;
      }
      const newUID = this.generateUserUID();
      writePersistentLocalStorage("user_uid", newUID);
      return newUID;
    },

    async loginUserWeb3() {
      const isWalletConnected = await web3OnboardManager.connectWallet();
      if (isWalletConnected) {
        try {
          const primaryWallet = web3OnboardManager.getPrimaryWallet();
          if (!primaryWallet) {
            createErrorNotify({
              message: "Wallet is not connected.",
            });
            throw new Error("Wallet is not connected");
          }

          const walletAddress = primaryWallet.accounts?.[0].address;
          const provider = primaryWallet.provider;
          if (!provider) {
            createErrorNotify({
              message: "Authentication failed.",
            });
            console.error("Authentication failed:");
            return;
          }
          // Шаг 1: Генерация сообщения для подписи
          const message = `Login request for address ${walletAddress} at ${new Date().toISOString()}`;

          // Шаг 2: Подписание сообщения
          const signature = await provider.request({
            method: "personal_sign",
            params: [message, walletAddress],
          });

          // Шаг 3: Формирование DTO
          const authenticateDTO: AuthenticateDTO = {
            wallet: walletAddress,
            message,
            signature,
          };

          // Шаг 4: Отправка данных на сервер
          const res =
            await apiClient.api.authWeb3ControllerAuthenticate(authenticateDTO);
          if (!res.ok) return;
          const token = useCookie("acc_token");
          const refreshToken = useCookie("acc_refresh_token");
          token.value = res.data?.accessToken;
          refreshToken.value = res.data?.refreshToken;
          nextTick(() => {
            this.fetchUserData();
          });
          return true;
        } catch (error) {
          console.error(error);
          createErrorNotify({
            message: "Authentication failed.",
          });
          console.error("Authentication failed:", error);
        }
      } else {
        web3OnboardManager.isLoading.value = false;
        console.error("Wallet not connected");
      }
    },

    createDefaultUserName() {
      if (!this.currentUser.firstName && !this.currentUser.lastName) {
        const el = userNames[Math.floor(Math.random() * userNames.length)];
        this.currentUser.firstName = el.firstName;
        this.currentUser.lastName = el.lastName;
      }
    },

    getPortfolioClass() {
      const state = this;
      class Portfolio {
        async getUserPortfolio() {
          try {
            state.portfolioLoading = true;
            const res = await apiClient.api.portfolioControllerCreate();
            state.portfolioLoading = false;
            if (!res.ok || !res.data?.unitsCount) return null;
            state.userPortfolio = res.data;
            return res.data;
          } catch (error) {
            console.error(error);
            if (error?.message == "Not Found") {
              return null;
            }
            createErrorNotify({
              message: "Something went wrong. Please try later.",
            });
            return null;
          }
        }
        async addPortfolioToken(data: UpsertPortfolioManualTokenDTO) {
          try {
            const res =
              await apiClient.api.portfolioManualTokenControllerUpsertManualToken(
                data
              );
            return res.data;
          } catch (error) {
            console.error(error);
            console.log("error: ", error);
          }
        }

        async deletePortfolioToken(id?: string) {
          if (!id) return;
          try {
            await apiClient.api.portfolioManualTokenControllerDeleteManualToken(
              id
            );
          } catch (error) {
            console.error(error);
            console.log("error: ", error);
          }
        }

        async updateWholePortfolio() {
          try {
            await apiClient.api.portfolioControllerUpdate();
            this.getUserPortfolio();
          } catch (error) {
            console.error(error);
            console.log("error: ", error);
          }
        }

        async getManualTokens() {
          try {
            const res =
              await apiClient.api.portfolioManualTokenControllerRetrieveManualToken();
            return res.data;
          } catch (error) {
            console.error(error);
            console.log("error: ", error);
          }
        }
      }
      return new Portfolio();
    },
  },
});

const createErrorNotify = (args: { message: string; delay?: number }) => {
  const errorNotify = new Notification({
    type: "error",
    message: args.message,
    timeout: args.delay ?? 5000,
  });
  errorNotify.createNewToast();
};
